import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import StaffThreadList from './components/StaffThreadList';
import StaffConversationView from './components/StaffConversationView';
import BroadcastModal from './components/BroadcastModal';
import { messagingService } from '../../lib/services';

const StaffMessagingHub = () => {
  const { userProfile } = useAuth();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [isBroadcastOpen, setIsBroadcastOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [filterRole, setFilterRole] = useState('all');

  useEffect(() => {
    loadThreads();
    setupRealtimeSubscription();

    return () => {
      if (subscription) {
        subscription?.unsubscribe();
      }
    };
  }, []);

  const loadThreads = async () => {
    setLoading(true);
    const { data, error } = await messagingService?.getUserThreads();
    if (!error && data) {
      // Filter for staff-related threads
      const staffThreads = data?.filter(thread => 
        thread?.participants?.some(p => 
          ['staff', 'manager', 'owner', 'sysadmin']?.includes(p?.user_profile?.role)
        )
      );
      setThreads(staffThreads);
    }
    setLoading(false);
  };

  const setupRealtimeSubscription = () => {
    const sub = messagingService?.subscribeToUserThreads(() => {
      loadThreads();
    });
    setSubscription(sub);
  };

  const handleThreadSelect = (thread) => {
    setSelectedThread(thread);
    messagingService?.markThreadAsRead(thread?.id);
  };

  const handleSendMessage = async (content) => {
    if (!selectedThread) return;
    const { data, error } = await messagingService?.sendMessage(selectedThread?.id, content);
    return { data, error };
  };

  const handleBroadcast = async (subject, message, targetRoles) => {
    // Get all users with target roles
    const { data: users } = await messagingService?.getAllUsers();
    const targetUsers = users?.filter(u => targetRoles?.includes(u?.role));
    
    if (targetUsers?.length === 0) {
      return { error: { message: 'No users found with selected roles' } };
    }

    // Create thread with all target users
    const { data: thread, error } = await messagingService?.createThread(
      `[BROADCAST] ${subject}`,
      targetUsers?.map(u => u?.id)
    );

    if (!error && thread) {
      // Send initial message
      await messagingService?.sendMessage(thread?.id, message);
      await loadThreads();
      setIsBroadcastOpen(false);
    }

    return { data: thread, error };
  };

  const filteredThreads = filterRole === 'all' 
    ? threads 
    : threads?.filter(thread => 
        thread?.participants?.some(p => p?.user_profile?.role === filterRole)
      );

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <div className="page-content-wrapper">
        <DashboardNavigation />
      </div>

      <main className="container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                Staff Messaging Hub
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                Real-time communication center for staff coordination and team updates
              </p>
            </div>
            <Button
              onClick={() => setIsBroadcastOpen(true)}
              iconName="Radio"
              iconPosition="left"
            >
              Broadcast
            </Button>
          </div>

          {/* Filter Bar */}
          <div className="bg-card rounded-xl p-4 shadow-md border border-border mb-6">
            <div className="flex items-center gap-4">
              <Icon name="Filter" size={20} color="var(--color-muted-foreground)" />
              <div className="flex gap-2 flex-wrap">
                {[
                  { value: 'all', label: 'All Staff' },
                  { value: 'staff', label: 'Staff' },
                  { value: 'manager', label: 'Managers' },
                  { value: 'owner', label: 'Owners' },
                  { value: 'sysadmin', label: 'Admins' }
                ]?.map(filter => (
                  <button
                    key={filter?.value}
                    onClick={() => setFilterRole(filter?.value)}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-colors
                      ${filterRole === filter?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground hover:bg-muted/80'
                      }
                    `}
                  >
                    {filter?.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <StaffThreadList
                threads={filteredThreads}
                selectedThread={selectedThread}
                onThreadSelect={handleThreadSelect}
                loading={loading}
                currentUserId={userProfile?.id}
              />
            </div>

            <div className="lg:col-span-2">
              {selectedThread ? (
                <StaffConversationView
                  thread={selectedThread}
                  onSendMessage={handleSendMessage}
                  currentUserId={userProfile?.id}
                />
              ) : (
                <div className="bg-card rounded-xl p-12 shadow-md border border-border h-full flex flex-col items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center mb-4">
                    <Icon name="Radio" size={40} color="var(--color-muted-foreground)" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    No Conversation Selected
                  </h3>
                  <p className="text-sm text-muted-foreground text-center max-w-sm">
                    Select a staff conversation or broadcast a message to your team
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {isBroadcastOpen && (
        <BroadcastModal
          onClose={() => setIsBroadcastOpen(false)}
          onBroadcast={handleBroadcast}
        />
      )}
    </div>
  );
};

export default StaffMessagingHub;